import React, { useState } from 'react';

const STEPS = [
  {
    title: 'Paso 1: Registra tu Rodeo',
    subtitle: 'Importa o registra tus animales para empezar',
    description: 'Podes cargar todos tus animales de una vez usando una planilla Excel, o registrarlos uno por uno desde el formulario. La fecha de nacimiento es opcional.',
    actions: [
      { icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12', label: 'Importar desde Excel', color: 'bg-emerald-100 text-emerald-700' },
      { icon: 'M12 4v16m8-8H4', label: 'Registrar manualmente', color: 'bg-blue-100 text-blue-700' },
    ],
    tip: 'Si ya tenes una planilla con tus animales, la importacion masiva es la forma mas rapida.',
    bg: 'from-emerald-50 to-green-50',
    accent: 'bg-emerald-600',
  },
  {
    title: 'Paso 2: Carga Pesajes y Eventos',
    subtitle: 'Registra la informacion productiva de tu rodeo',
    description: 'Cada pesaje activa automaticamente el calculo de GMD, Frame Score, y alertas. Tambien podes registrar servicios, pariciones, eventos sanitarios y controles de machos.',
    actions: [
      { icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3', label: 'Pesajes', color: 'bg-blue-100 text-blue-700' },
      { icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z', label: 'Servicios y Pariciones', color: 'bg-pink-100 text-pink-700' },
      { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Eventos Campo', color: 'bg-amber-100 text-amber-700' },
    ],
    tip: 'Podes cargar pesajes masivamente por Excel. El sistema calcula la GMD automaticamente.',
    bg: 'from-blue-50 to-indigo-50',
    accent: 'bg-blue-600',
  },
  {
    title: 'Paso 3: Tu Dashboard Inteligente',
    subtitle: 'Deja que la IA analice tu rodeo',
    description: 'El dashboard te muestra KPIs en tiempo real, alertas criticas automaticas, eficiencia productiva, Frame Score y decision de invernada. La IA analiza tu rodeo y te da recomendaciones.',
    actions: [
      { icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', label: 'Dashboard con KPIs', color: 'bg-green-100 text-green-700' },
      { icon: 'M13 10V3L4 14h7v7l9-11h-7z', label: 'Alertas Automaticas', color: 'bg-red-100 text-red-700' },
      { icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', label: 'Asistente IA', color: 'bg-purple-100 text-purple-700' },
    ],
    tip: 'Podes generar un Reporte PDF con IA desde el Dashboard y enviarlo por email.',
    bg: 'from-purple-50 to-fuchsia-50',
    accent: 'bg-purple-600',
  },
];

const OnboardingTutorial = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const current = STEPS[step];

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      localStorage.setItem('tkx_onboarding_done', 'true');
      onComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('tkx_onboarding_done', 'true');
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" data-testid="onboarding-overlay">
      <div className={`bg-gradient-to-br ${current.bg} rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden relative`} data-testid="onboarding-card">
        <button onClick={handleSkip} aria-label="Cerrar tutorial"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-gray-600 hover:bg-white hover:text-gray-900 shadow transition z-10"
          data-testid="onboarding-close-x">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {/* Progress bar */}
        <div className="flex gap-1.5 p-4 pb-0 pr-12">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= step ? current.accent : 'bg-gray-200'}`} />
          ))}
        </div>

        {/* Content */}
        <div className="p-6 pt-4">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold ${current.accent} text-white px-2 py-0.5 rounded-full`}>{step + 1} / {STEPS.length}</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="onboarding-title">{current.title}</h2>
          <p className="text-sm font-medium text-gray-500 mb-4">{current.subtitle}</p>
          <p className="text-sm text-gray-700 leading-relaxed mb-5">{current.description}</p>

          {/* Action cards */}
          <div className="space-y-2 mb-5">
            {current.actions.map((action, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 backdrop-blur-sm">
                <div className={`w-9 h-9 ${action.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-800">{action.label}</span>
              </div>
            ))}
          </div>

          {/* Tip */}
          <div className="bg-white/50 rounded-lg p-3 mb-5 border border-white/60">
            <p className="text-xs text-gray-600"><strong className="text-gray-800">Tip:</strong> {current.tip}</p>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between">
            <button onClick={handleSkip} className="text-sm font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900 transition" data-testid="onboarding-skip">
              Saltar tutorial
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition" data-testid="onboarding-prev">
                  Anterior
                </button>
              )}
              <button onClick={handleNext}
                className={`px-6 py-2 ${current.accent} text-white text-sm font-semibold rounded-lg hover:opacity-90 transition`}
                data-testid="onboarding-next">
                {step === STEPS.length - 1 ? 'Comenzar' : 'Siguiente'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
